// const axios = require('axios').default;
import axios from 'axios';

const state = {
  api_url: '',
  error_bag: {},
};

// getters
const getters = {
  base_url(state, getters, rootState) {
    return state.api_url;
  },
  exercise_videos_url(state, getters, rootState) {
    return state.api_url.replace('/api/v1', '') + '/images/exercises';
  }
};

// actions
const actions = {
  getEndpoint({state, commit, dispatch, getters, rootState}, payload) {
    payload.method = 'get';
    return new Promise((resolve, reject) => {
      dispatch('callApi', payload)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
          });
    });
  },
  postEndpoint({state, commit, dispatch, getters, rootState}, payload) {
    payload.method = 'post';
    return new Promise((resolve, reject) => {
      dispatch('callApi', payload)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
          });
    });
  },
  putEndpoint({state, commit, dispatch, getters, rootState}, payload) {
    payload.method = 'put';
    return new Promise((resolve, reject) => {
      dispatch('callApi', payload)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
          });
    });
  },
  deleteEndpoint({state, commit, dispatch, getters, rootState}, payload) {
    payload.method = 'delete';
    return new Promise((resolve, reject) => {
      dispatch('callApi', payload)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
          });
    });
  },
  // dummy_action ({state, commit, dispatch, getters, rootState}, payload) {
  /**
   * @param payload.suppress_error_toast boolean
   *   Opt out of the GLOBAL danger toast for this one call -- and out of nothing else.
   *
   *   onError() below fires a danger toast for every rejected request, so a caller that
   *   wants to say something more specific about a failure would otherwise produce two
   *   stacked toasts for one failed action. The notification-preferences screen is the
   *   first such caller: it emits `{label} kon niet worden opgeslagen`, which names what
   *   failed, where the global handler emits either a raw server `message` or a generic
   *   network string. The UI contract (39-UI-SPEC, "Exactly one message per failed
   *   action") forbids both at once, so one of them has to stand down -- and the specific
   *   one is the one worth keeping.
   *
   *   This flag is read from the callApi payload, not from `payload.data`, so it never
   *   reaches the wire.
   */
  callApi({state, commit, dispatch, getters, rootState, rootGetters}, payload) {

    commit('clear_error_bag');

    // Verify the contents of the payload
    if (typeof payload.data === "undefined") {
      payload.data = {};
    }

    payload.data.app_version = import.meta.env.PG_APP_VERSION;

    dispatch('app/processing', true, {root: true});

    return new Promise((resolve, reject) => {
      axios({
        method: payload.method,
        "headers": {
          'Authorization': 'Bearer ' + rootGetters['current_user/token'],
          "Content-Type": 'application/json',
          "Accept": "application/json"
        },
        url: state.api_url + payload.endpoint,
        data: payload.method !== 'get' ? payload.data : null,
        params: payload.method === 'get' ? payload.data : null,
      }).then(response => {
        dispatch('onSuccess', response);
        resolve(response);
      }).catch(error => {
        // OPT-IN, PER-DISPATCH TOAST SUPPRESSION. Suppresses the global danger
        // toast for THIS request only.
        //
        // MERGE NOTE (2026-08-14, phase 39 <- phase 43). Phases 39 and 43 each
        // built this feature independently and they did NOT agree:
        //   - 39 shipped `suppress_error_toast`, which reaches `onError` as
        //     { error, suppress_toast } and silences ONLY the toast.
        //   - 43 shipped `silent`, which skipped the `dispatch('onError', …)`
        //     call entirely — and therefore also skipped the error bag, the
        //     ECONNABORTED branch and every other unconditional thing `onError`
        //     does. That was over-broad: 43's own note below says "not error
        //     swallowing", and skipping the dispatch made it exactly that.
        // Resolved onto 39's mechanism, which is the correct one. `silent` is
        // kept as an accepted spelling so 43's call site and its tests are
        // unchanged, but it now means "suppress the toast", not "skip onError".
        //
        // What it is NOT:
        //   - not a default. Every existing call site passes no flag and keeps
        //     toasting exactly as it did. Adding it to a call site is a
        //     deliberate act, and needs a reason written at that call site.
        //   - not error swallowing. `onError` still runs, and the promise below
        //     still rejects, so a caller that wants to know about the failure can.
        //
        // Two call sites use it today: `news/fetch_all`, the member's
        // supplementary news panel, which must not put a red bar over a working
        // reservations page (43-UI-SPEC "The one-message rule"); and the
        // notification-preferences screen, which emits its own specific message
        // (39-UI-SPEC "Exactly one message per failed action").
        //
        // Do NOT widen this into a general "quiet mode". This project has spent
        // two phases learning that a green surface hiding a real failure is
        // worse than a noisy one; a broad suppression flag is how that
        // regression gets reintroduced.
        //
        // The flag lives on `payload`, never on `payload.data` — `payload.data`
        // is assigned straight to axios's `data` for a write and to `params` for
        // a read, so a flag placed there would be serialised into every POST
        // body and appended as `?silent=true` to every GET (T-43-32).
        dispatch('onError', {
          error,
          suppress_toast: payload.suppress_error_toast === true || payload.silent === true,
        }).catch(error => {});

        reject(error.response || error);
      }).finally(() => {
        dispatch('app/processing', false, {root: true});
      });
    });
  },
  onSuccess({state, commit, dispatch, getters, rootState}, response) {
    // This one ensures compatibility with the Old notifications
    // They send 200 responses regardless, so we catch them here and show
    // the notifications in Vue
    if(process.env.NODE_ENV !== 'production') {
      console.log(response);
    }
  },
  onError({state, commit, dispatch, getters, rootState}, payload) {
    // Accepts either a raw axios error (the historical shape, still used by any direct
    // dispatch('api/onError', error)) or { error, suppress_toast } -- see callApi.
    //
    // Suppression covers the TOAST ONLY. Everything else below stays unconditional.
    const error = payload?.error ?? payload;
    const suppress_toast = payload?.suppress_toast === true;

    // // We catch a timeout here. The value of which is set
    // // in the bootstrap.js file.
    if (error.code === 'ECONNABORTED') {
      if (!suppress_toast) {
        dispatch('notifications/addDangerNotification', {
          message: 'Connectie Timeout. Herlaad de pagina en probeer opnieuw.'
        }, {root: true});
      }
    }

    // Guard against network errors where error.response is undefined (axios 1.x)
    else if (!error.response) {
      if (!suppress_toast) {
        dispatch('notifications/addDangerNotification', {
          message: 'Netwerkfout. Controleer je verbinding en probeer opnieuw.'
        }, {root: true});
      }
    }

    // If there is an error bag, we fill it
    else if (typeof error.response.data !== 'undefined' && typeof error.response.data.errors !== "undefined") {
      // Unconditional: forms elsewhere read the error bag to mark their own fields.
      // Opting out of a message is not opting out of validation state.
      commit('fill_error_bag', {errors: error.response.data.errors, status: error.response.headers.status});

      let errors = error.response.data.errors;
      let errorMessage = '';

      Object.keys(errors).forEach((error_key, index) => {
        errorMessage = errorMessage + errors[error_key].join(' ') + (index !== Object.keys(errors).length - 1 ? '<br/>' : '');
      });

      if (!suppress_toast) {
        dispatch('notifications/addDangerNotification', {
          message: errorMessage,
        }, {root: true});
      }
    }
    else if(error.response.status === 401) {
      if (!suppress_toast) {
        dispatch('notifications/addDangerNotification', {
          message: error.response.data.message,
          hide_after: 10000,
        }, {root: true});
      }
      // DELIBERATELY OUTSIDE the suppression check, and it must stay there:
      // suppressing a toast must never suppress a session invalidation. A caller
      // opting out of a *message* has not opted out of being *logged out*.
      dispatch('current_user/logout',{},{root:true});
    }
    else if(error.response.status === 409) {
      // Belangrijk bericht, tonen we langer
      if (!suppress_toast) {
        dispatch('notifications/addDangerNotification', {
          message: error.response.data.message,
          hide_after: 60000,
        }, {root: true});
      }
    }
    // This global error catcher allows for specific triggers based on the error
    // Useful for error bags, auth flows, stuff that can pop up all over
    else if (error.response.status >= 400) {
      if (!suppress_toast) {
        dispatch('notifications/addDangerNotification', {
          message: error.response.data.message,
          hide_after: 5000,
        }, {root: true});
      }
    }
  }
};

// mutations
const mutations = {
  fill_error_bag(state, data) {
    let error_bag = {};

    Object.keys(data.errors).forEach((key) => {
      error_bag[key] = data.errors[key];
    });

    state.error_bag = error_bag;
  },
  set_api_url(state, value) {
    state.api_url = value;
  },
  clear_error_bag(state) {
    state.error_bag = {};
  }
};

export default {
  namespaced: true,

  state,
  getters,
  actions,
  mutations
};
